import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import { ThemeProvider } from "next-themes";
import { LGNSInputsProvider } from "@/context/lgns-inputs";
import App from "./App";
import "./index.css";
import { injectBrandColors } from "./lib/inject-brand-colors";
import { fetchAndApplyTenantConfig, onTenantConfigUpdate } from "./lib/tenant-config";
import { initAnalytics } from "./lib/analytics";

initAnalytics();
injectBrandColors();

onTenantConfigUpdate(() => {
  injectBrandColors();
});

fetchAndApplyTenantConfig();

// Auto-reload when Vite can't fetch a lazy chunk — happens when a new
// deploy renames chunks while the user still has the old HTML cached.
window.addEventListener('vite:preloadError', () => {
  window.location.reload();
});

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    sendDefaultPii: true,
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // Suppress unactionable wagmi-internal errors thrown when certain wallet
    // providers don't implement optional methods (setDefaultChain, request).
    // These originate inside wagmi's connector event handlers and cannot be
    // caught at the application level.
    ignoreErrors: [
      /this\.getProvider\(i\)\.(setDefaultChain|request) is not a function/,
      /undefined is not an object \(evaluating 'this\.getProvider\(i\)\./,
      /No matching key\. session topic doesn't exist/,
    ],
    beforeSend(event) {
      const tenantSlug = import.meta.env.VITE_TENANT_SLUG;
      if (tenantSlug) {
        event.tags = { ...event.tags, tenant_slug: tenantSlug };
      }
      event.tags = { ...event.tags, route: window.location.pathname };
      return event;
    },
  });
}

export function setSentryWallet(address: string | null) {
  if (address) {
    Sentry.setUser({ id: address });
    Sentry.setTag('wallet', address);
  } else {
    Sentry.setUser(null);
  }
}

createRoot(document.getElementById("root")!).render(
  <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
    <LGNSInputsProvider>
      <App />
    </LGNSInputsProvider>
  </ThemeProvider>
);
